import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Button, Modal, Table, Tag, message } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import { useTranslation } from '../../../i18n';
import { useSelectedShop } from '../layout/selectedShopContext';
import {
  createContentTemplate,
  deleteContentTemplate,
  getContentTemplateDetail,
  getContentTemplates,
  updateContentTemplate
} from '../../../store/request';
import type { TemplateChannel, TemplateDetail, TemplateListItem } from '../../../types/template';
import { ScriptDetailModal } from './ScriptDetailModal';
import { ScriptEditModal, type ScriptDraft } from './ScriptEditModal';
import { ScriptGenerateModal } from './ScriptGenerateModal';
import { scriptChannelMeta, type ScriptChannel } from './scriptData';

function toApiChannel(channel: ScriptChannel): TemplateChannel {
  if (channel === 'email') return 'EMAIL';
  return 'DM';
}

function toScriptChannel(channel?: string): ScriptChannel | null {
  const value = channel?.trim().toUpperCase();
  if (value === 'EMAIL') return 'email';
  if (value === 'DM') return 'dm';
  return null;
}

function draftFromDetail(detail: TemplateDetail): ScriptDraft | null {
  if (detail.id == null) return null;
  const channel = toScriptChannel(detail.channel);
  if (!channel) return null;
  return {
    id: String(detail.id),
    name: detail.name ?? '',
    channel,
    content: detail.body ?? '',
    ...(detail.subject?.trim() ? { subject: detail.subject.trim() } : {})
  };
}

function formatTime(value?: string) {
  if (!value) return '—';
  const parsed = dayjs(value);
  if (!parsed.isValid()) return value;
  return parsed.format('YYYY-MM-DD HH:mm:ss');
}

export function Script() {
  const { t } = useTranslation();
  const { shopId } = useSelectedShop();
  const [templates, setTemplates] = useState<TemplateListItem[]>([]);
  const [total, setTotal] = useState(0);
  const [pageNum, setPageNum] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [loading, setLoading] = useState(false);
  const [draft, setDraft] = useState<ScriptDraft | null>(null);
  const [detailId, setDetailId] = useState<number>();
  const [editLoadingId, setEditLoadingId] = useState<number>();
  const [deletingId, setDeletingId] = useState<number>();
  const [generateOpen, setGenerateOpen] = useState(false);
  const [listVersion, setListVersion] = useState(0);

  const openEdit = useCallback(
    async (templateId: number) => {
      const currentShopId = shopId?.trim();
      if (!currentShopId) {
        message.warning(t('script.shop_required'));
        return;
      }
      setEditLoadingId(templateId);
      try {
        const res = await getContentTemplateDetail(templateId, { shopId: currentShopId });
        if (res.code !== 200 || !res.content) {
          if (res.code !== -1) message.error(res.message || t('script.detail_load_failed'));
          return;
        }
        const next = draftFromDetail(res.content);
        if (!next) {
          message.error(t('script.detail_load_failed'));
          return;
        }
        setDetailId(undefined);
        setDraft(next);
      } catch {
        message.error(t('script.detail_load_failed'));
      } finally {
        setEditLoadingId(undefined);
      }
    },
    [shopId, t]
  );

  const confirmDelete = useCallback(
    (row: TemplateListItem) => {
      const templateId = row.id;
      const currentShopId = shopId?.trim();
      if (templateId == null) return;
      if (!currentShopId) {
        message.warning(t('script.shop_required'));
        return;
      }
      const name = row.name?.trim() || '—';
      Modal.confirm({
        title: t('common.delete'),
        content: t('script.delete_confirm', { name }),
        okText: t('common.confirm'),
        cancelText: t('common.cancel'),
        okButtonProps: { danger: true },
        centered: true,
        onOk: async () => {
          setDeletingId(templateId);
          try {
            const res = await deleteContentTemplate(templateId, { shopId: currentShopId });
            if (res.code !== 200) {
              if (res.code !== -1) message.error(res.message || t('script.delete_failed'));
              return Promise.reject(new Error(res.message || 'delete failed'));
            }
            message.success(res.message || t('script.delete_success'));
            if (detailId === templateId) setDetailId(undefined);
            if (draft?.id === String(templateId)) setDraft(null);
            setListVersion((version) => version + 1);
          } finally {
            setDeletingId(undefined);
          }
        }
      });
    },
    [detailId, draft?.id, shopId, t]
  );

  useEffect(() => {
    setPageNum(1);
  }, [shopId]);

  useEffect(() => {
    const currentShopId = shopId?.trim();
    if (!currentShopId) {
      setTemplates([]);
      setTotal(0);
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    getContentTemplates({ shopId: currentShopId, pageNum, pageSize })
      .then((res) => {
        if (cancelled) return;
        if (res.code !== 200) {
          message.error(res.message || t('script.load_failed'));
          setTemplates([]);
          setTotal(0);
          return;
        }
        const records = res.content?.records;
        const pageTotal = res.content?.total;
        setTemplates(Array.isArray(records) ? records : []);
        setTotal(typeof pageTotal === 'number' ? pageTotal : 0);
      })
      .catch(() => {
        if (!cancelled) {
          message.error(t('script.load_failed'));
          setTemplates([]);
          setTotal(0);
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [shopId, pageNum, pageSize, listVersion, t]);

  const save = async (next: ScriptDraft) => {
    const currentShopId = shopId?.trim();
    if (!currentShopId) {
      message.warning(t('script.shop_required'));
      return;
    }
    const channel = toApiChannel(next.channel);
    const body = {
      shopId: currentShopId,
      name: next.name,
      channel,
      body: next.content,
      ...(next.subject ? { subject: next.subject } : {})
    };
    if (next.id) {
      const templateId = Number(next.id);
      if (!Number.isInteger(templateId) || templateId <= 0) {
        message.error(t('script.update_failed'));
        return;
      }
      const res = await updateContentTemplate(templateId, body);
      if (res.code !== 200) {
        if (res.code !== -1) message.error(res.message || t('script.update_failed'));
        return;
      }
      message.success(res.message || t('script.saved'));
      setDraft(null);
      setListVersion((version) => version + 1);
      return;
    }
    const res = await createContentTemplate(body);
    if (res.code !== 200) {
      if (res.code !== -1) message.error(res.message || t('script.create_failed'));
      return;
    }
    message.success(res.message || t('script.created'));
    setDraft(null);
    setListVersion((version) => version + 1);
  };

  const columns: ColumnsType<TemplateListItem> = useMemo(
    () => [
      {
        title: t('script.name'),
        dataIndex: 'name',
        render: (name?: string) => <span className="font-medium text-slate-800">{name?.trim() || '—'}</span>
      },
      {
        title: t('script.channel'),
        dataIndex: 'channel',
        render: (channel?: string) => {
          const scriptChannel = toScriptChannel(channel);
          if (!scriptChannel) return channel?.trim() || '—';
          return (
            <Tag className={`m-0 border-0 ${scriptChannelMeta[scriptChannel].className}`}>
              {t(`script.channel_${scriptChannel}`)}
            </Tag>
          );
        }
      },
      {
        title: t('script.col_updated'),
        dataIndex: 'mtime',
        render: (value?: string) => <span className="text-slate-500">{formatTime(value)}</span>
      },
      {
        title: t('script.actions'),
        key: 'actions',
        render: (_, row) => (
          <div className="inline-flex items-center gap-1">
            <Button
              type="link"
              className="wb-text-link"
              disabled={row.id == null}
              onClick={() => {
                if (row.id == null) return;
                setDetailId(row.id);
              }}
            >
              {t('common.detail')}
            </Button>
            <Button
              type="link"
              className="wb-text-link"
              disabled={row.id == null}
              loading={row.id != null && editLoadingId === row.id}
              onClick={() => {
                if (row.id == null) return;
                void openEdit(row.id);
              }}
            >
              {t('common.edit')}
            </Button>
            <Button
              type="link"
              danger
              className="!px-1"
              disabled={row.id == null}
              loading={row.id != null && deletingId === row.id}
              onClick={() => confirmDelete(row)}
            >
              {t('common.delete')}
            </Button>
          </div>
        )
      }
    ],
    [confirmDelete, deletingId, editLoadingId, openEdit, t]
  );

  return (
    <div className="container">
      <header className="mb-4 flex flex-wrap items-baseline gap-x-3 gap-y-1">
        <h1 className="mb-0 mt-0 text-[22px] font-semibold leading-snug tracking-tight text-slate-800">{t('script.title')}</h1>
      </header>

      <div className="mb-3 flex flex-wrap items-center gap-2">
        <Button
          type="primary"
          className="wb-btn-primary rounded-lg"
          onClick={() => setDraft({ name: '', channel: 'dm', content: '' })}
        >
          {t('script.new_template')}
        </Button>
        {/* <Button className="rounded-lg" icon={<RobotOutlined />} onClick={() => setGenerateOpen(true)}>
          {t('script.ds_generate')}
        </Button> */}
      </div>

      <section className="wb-section script-table !p-0 overflow-hidden">
        <Table
          rowKey={(row) => (row.id != null ? String(row.id) : `${row.shopId ?? ''}-${row.name ?? ''}-${row.channel ?? ''}`)}
          columns={columns}
          dataSource={templates}
          loading={loading}
          pagination={{
            current: pageNum,
            pageSize,
            total,
            showSizeChanger: true,
            pageSizeOptions: ['10', '20', '50'],
            onChange: (current, size) => {
              if (size !== pageSize) {
                setPageNum(1);
                setPageSize(size);
                return;
              }
              setPageNum(current);
            }
          }}
        />
      </section>

      <ScriptDetailModal
        open={detailId != null}
        templateId={detailId}
        shopId={shopId}
        onClose={() => setDetailId(undefined)}
        onEdit={(detail: TemplateDetail) => {
          const next = draftFromDetail(detail);
          if (!next) return;
          setDetailId(undefined);
          setDraft(next);
        }}
      />
      <ScriptEditModal open={Boolean(draft)} draft={draft} onClose={() => setDraft(null)} onSave={save} />
      <ScriptGenerateModal
        open={generateOpen}
        onClose={() => setGenerateOpen(false)}
        onGenerated={({ channel, name, subject, content }) => {
          setGenerateOpen(false);
          setDraft({
            name,
            channel,
            content,
            ...(subject ? { subject } : {})
          });
        }}
      />
    </div>
  );
}
